import sleep from '/js/utils/sleep';
import { logError } from '/js/utils/logError';

class PrkPhotoSwipeVideo {
  constructor(lightbox, options = {}) {
    this.lightbox = lightbox;

    // Bind event listeners to class instance
    this.onDomItemData = this.onDomItemData.bind(this);
    this.onItemData = this.onItemData.bind(this);
    this.onIsContentZoomable = this.onIsContentZoomable.bind(this);
    this.onContentLoad = this.onContentLoad.bind(this);
    this.onContentActivate = this.onContentActivate.bind(this);
    this.onContentDeactivate = this.onContentDeactivate.bind(this);
    this.onContentRemove = this.onContentRemove.bind(this);
    this.onContentDestroy = this.onContentDestroy.bind(this);

    this.init();
  }

  init() {
    this.attachEventListeners();
  }

  attachEventListeners() {
    this.lightbox.addFilter('domItemData', this.onDomItemData);
    this.lightbox.addFilter('itemData', this.onItemData);
    this.lightbox.addFilter('isContentZoomable', this.onIsContentZoomable);
    this.lightbox.on('contentLoad', this.onContentLoad);
    this.lightbox.on('contentActivate', this.onContentActivate);
    this.lightbox.on('contentDeactivate', this.onContentDeactivate);
    this.lightbox.on('contentRemove', this.onContentRemove);
    this.lightbox.on('contentDestroy', this.onContentDestroy);
  }

  onDomItemData(itemData, element, linkEl) {
    // Init videos from video-list button
    // @TODO: add support for separate youtube or prk videos, not just video stack (one button, many videos)
    if (element.classList.contains('js-product-videos')) {
      itemData.type = 'video-list';
      try {
        const videosData = JSON.parse(element.querySelector('script[type="application/json"]')?.textContent);
        if (videosData) {
          itemData.videoData = videosData;
        }
      } catch (e) {
        logError('Failed to parse PRK video data', e);
      }
    }

    return itemData;
  }

  onItemData(itemData, index) {
    // Init videos from video-list button
    // @TODO: add support for separate youtube or prk videos, not just video stack (one button, many videos)
    if (itemData.type === 'video-list') {
      if (!itemData.videoData || !(itemData.videoData instanceof Array) || !itemData.videoData.length) {
        // Manifest type as prk-video without data, shall be rendered as error
        itemData.type = 'prk-video';
        return itemData;
      }

      const itemDataList = [];
      for (const videoData of itemData.videoData) {
        switch (videoData.type) {
          case 'prk':
            itemDataList.push({
              elemesnt: itemData.element,
              type: 'prk-video',
              msrc: videoData.preview,
              thumbWidth: 120,
              thumbHeight: 120,
              posterSrc: videoData.poster,
              width: videoData?.sources[0]?.width || 1280,
              height: videoData?.sources[0]?.height || 720,
              sources: videoData?.sources || [],
              alt: videoData.title || undefined,
            });

            break;
          case 'youtube':
            itemDataList.push({
              element: itemData.element,
              type: 'youtube',
              videoId: videoData.videoId,
              msrc: videoData.preview,
              thumbWidth: 120,
              thumbHeight: 120,
              width: 1280, // Setup for correct bounding and aspect ratio
              height: 720, // Setup for correct bounding and aspect ratio
              alt: videoData.title || undefined,
            });

            break;
        }
      }

      if (itemDataList.length) {
        itemData = itemDataList[0];
        this.lightbox.pswp.options.dataSource.items.splice(index, 1, ...itemDataList);
      }
    }

    return itemData;
  }

  onIsContentZoomable(isContentZoomable, content) {
    if (this.isVideo(content.type))
      return false;
    
    return isContentZoomable;
  }

  onContentLoad(event) {
    const { content } = event;

    // Only process videos
    if (!this.isVideo(content.type))
      return;

    // Prevent the deafult behavior
    event.preventDefault();

    // Init custom container
    content.element = document.createElement('div');
    content.element.className = `prk-pswp-video prk-pswp-video--${this.isPrkVideo ? 'prk' : 'youtube'}`;
  }

  onContentActivate({ content }) {
    if (this.isVideo(content.type)) {
      this.loadVideo(content);
    }
  }

  onContentDeactivate({ content }) {
    if (this.isVideo(content.type) && content.data.pkrPlayer) {
      content.data.pkrPlayer.player.pause();
    }
  }

  onContentRemove({ content }) {
    if (this.isVideo(content.type)) {
      this.unloadVideo(content);
    }
  }

  onContentDestroy({ content }) {
    if (this.isVideo(content.type)) {
      this.unloadVideo(content);
    }
  }

  /**
   * @param {string} videoId
   * @returns {string}
   */
  getYoutubeLink(videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=0&autohide=1&rel=0`;
  }

  /**
   * @param {HTMLElement} element
   */
  configureFailedVideoElement(element) {
      element.classList.add('prk-pswp-video--failed');
      element.innerHTML = '<div class="prk-pswp-video__error-message">Видео не доступно. Попробуйте позже</div>';
  }

  async loadVideo(content) {
    const isPrkVideo = this.isPrkVideo(content.type);

    if (isPrkVideo) {
      const { posterSrc, sources } = content.data;
      if (!posterSrc || !(sources instanceof Array) || !sources.length) {
        logError('Prk video lacks required data');
        this.configureFailedVideoElement(content.element);
        return;
      }
    } else {
      const videoId = content.data.videoId;
    if (!videoId) {
      logError('Youtube video must have videoId set');
      this.configureFailedVideoElement(content.element);
      return;
    }
    }

    // When player is already initialized, do not add its dulicate
    if (content.data.pkrPlayer)
      return;
    
    // Add spinner for better UX
    const spinner = document.createElement('i');
    spinner.className = 'spinner spinner--white prk-pswp-video__loading-spinner';
    content.element.appendChild(spinner);

    // Add video element
    const PrkPlayer = await import('/js/prk/prk-player').then(m => m.default);

    // Only continue if user hasn't changed the slide while player was loading
    if (this.lightbox.pswp.currIndex !== content.index) {
      // Remove spinner
      spinner.remove();
      return;
    }

    const video = isPrkVideo
      ? this.createPrkVideoElement(content)
      : this.createYoutubeVideoElement(content);

    // Start preloading video
    content.element.appendChild(video);
    content.data.pkrPlayer = new PrkPlayer(video);

    // Remove spinner when media is ready to be displayed
    content.data.pkrPlayer.player.once(isPrkVideo ? 'loadedmetadata' : 'ready', () => spinner.remove());
  }

  createPrkVideoElement(content) {
    const { posterSrc, sources } = content.data;

    const video = document.createElement('video');
    video.id = `prk-video-player_${content.index}`;
    video.classList.add('video');
    video.setAttribute('controls', 'controls');
    video.setAttribute('crossorigin', 'crossorigin');
    video.setAttribute('playsinline', 'playsinline');
    video.setAttribute('poster', posterSrc);
    video.innerHTML = `
        ${this.renderSources(sources)}
        <p>Ваш браузер не поддерживает видео, обновите его или попробуйте другой.</p>
    `;

    return video;
  }

  createYoutubeVideoElement(content) {
    const videoId = content.data.videoId;
    const video = document.createElement('div');
    video.id = `prk-video-player_${content.index}`;
    video.className = 'video plyr__video-embed';

    const iframe = document.createElement('iframe');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', 'allowfullscreen');
    iframe.setAttribute('allowtransparency', 'allowtransparency');
    iframe.setAttribute('videoid', videoId);
    iframe.setAttribute('src', this.getYoutubeLink(videoId));
    video.appendChild(iframe);
    return video;
  }

  async unloadVideo(content) {
    if (content.data.pkrPlayer) {
      content.data.pkrPlayer.player.destroy();
      content.data.pkrPlayer = null;
      // Using sleep allows prkPlayer to gracefully update HTML before clearing content
      await sleep();
    }

    content.element.innerHTML = '';
  }

  /**
   * @param {{ src: string, mimeType: string, width: number, height: number }} source
   */
  renderSource(source) {
    return `<source src="${source.src}" type="${source.mimeType}" size="${source.height}"/>`;
  }

  /**
   * @param {Array<{ src: string, mimeType: string, width: number, height: number }>} sources
   * @return {string}
   */
  renderSources(sources) {
    return sources.map(this.renderSource).join('');
  }

  isVideo(contentType) {
    return ['video-list', 'prk-video', 'youtube'].includes(contentType);
  }

  isPrkVideo(contentType) {
    return 'prk-video' === contentType;
  }

  isYoutubeVideo(contentType) {
    return 'youtube' === contentType;
  }
}

export { PrkPhotoSwipeVideo };
export default PrkPhotoSwipeVideo;
